import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { CardPhotoCarousel } from "../components/CardPhotoCarousel";
import { Coins, ArrowSquareOut, Eye, X } from "@phosphor-icons/react";
import ArtifactPublicDetail from "../components/ArtifactPublicDetail";

export default function PublicCatalog() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null); // artifact currently opened in modal

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/catalog/${token}`);
        setData(data);
      } catch (e) {
        setErr("Questo link non è più disponibile.");
      }
    })();
  }, [token]);

  // Lock body scroll while modal is open + close on Esc
  useEffect(() => {
    if (!selected) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [selected]);

  if (err) {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center">
        <h1 className="font-serif text-3xl text-[#1E2520] mb-3">Link non disponibile</h1>
        <p className="text-[#5C635A]">{err}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20">
        <div className="h-10 w-2/3 scanning" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="public-catalog">
      <header className="border-b border-black/5 bg-[#F9F8F6]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center bg-[#9C5B42] text-white">
              <Coins weight="duotone" size={20} />
            </span>
            <div className="leading-tight">
              <div className="font-serif text-2xl text-[#1E2520]">Reperti</div>
              <div className="label-eyebrow">Catalogo · sola lettura</div>
            </div>
          </div>
          <div className="text-xs text-[#5C635A] flex items-center gap-1.5">
            <Eye weight="duotone" size={14} /> {data.label}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="mb-10">
          <div className="label-eyebrow mb-3">Galleria condivisa</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1E2520]">
            {data.total} {data.total === 1 ? "reperto" : "reperti"} catalogati
          </h1>
          <p className="text-sm text-[#5C635A] mt-2">
            Clicca su un reperto per vedere foto, descrizione, aneddoto e valore.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mb-12">
          {data.items.map((it) => {
            const ident = it.identification || {};
            return (
              <button
                type="button"
                key={it.id}
                onClick={() => setSelected(it)}
                className="card overflow-hidden relative text-left hover:shadow-lg transition-shadow group focus:outline-none focus:ring-2 focus:ring-[#9C5B42]/40"
                data-testid={`public-catalog-card-${it.inventory_code || it.id}`}
              >
                {it.inventory_code && (
                  <span className="absolute top-3 left-3 z-10 bg-[#1E2520] text-[#F9F8F6] font-mono text-xs tracking-[0.15em] px-2 py-1 rounded-sm">
                    {it.inventory_code}
                  </span>
                )}
                <CardPhotoCarousel
                  photos={it.photos || []}
                  alt={ident.nome || "Reperto"}
                  testId={`public-card-carousel-${it.inventory_code || it.id}`}
                />
                <div className="p-4">
                  <div className="label-eyebrow mb-1">{ident.categoria}</div>
                  <h3 className="font-serif text-lg leading-tight text-[#1E2520] line-clamp-2">
                    {ident.nome || "Reperto"}
                  </h3>
                  <p className="text-xs text-[#5C635A] mt-1">{ident.periodo}</p>
                </div>
              </button>
            );
          })}
        </div>

        {data.items.length === 0 && (
          <div className="text-center text-[#5C635A] py-10">Catalogo vuoto.</div>
        )}

        <div className="border-t border-black/5 pt-8 text-center">
          <Link to="/" className="text-[#9C5B42] inline-flex items-center gap-1.5 hover:underline text-sm">
            Apri Reperti <ArrowSquareOut size={14} />
          </Link>
        </div>
      </main>

      {/* Detail modal */}
      {selected &&
        createPortal(
          <div
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 md:py-10 px-3 md:px-6"
            onClick={() => setSelected(null)}
            data-testid="public-catalog-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative bg-[#F9F8F6] w-full max-w-5xl rounded-sm shadow-2xl fade-in"
            >
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="sticky top-3 float-right mr-3 z-10 h-10 w-10 flex items-center justify-center bg-[#1E2520] hover:bg-[#9C5B42] text-white rounded-full transition-colors"
                aria-label="Chiudi dettaglio"
                data-testid="public-catalog-modal-close"
              >
                <X size={18} />
              </button>
              <div className="p-5 md:p-10 pt-2 md:pt-4">
                <ArtifactPublicDetail item={selected} />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
